import axios from "axios";
import { FileText, PlayCircle, Headphones, Link2 } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const http = axios.create({ baseURL: API, withCredentials: true });

export const TYPE_META = {
    pdf: { label: "PDF", action: "Download", Icon: FileText, tint: "#EEF0E6" },
    video: { label: "Video", action: "Watch", Icon: PlayCircle, tint: "#EFEAEF" },
    audio: { label: "Audio", action: "Listen", Icon: Headphones, tint: "#F1ECE3" },
    link: { label: "Link", action: "Open", Icon: Link2, tint: "#ECEEE8" },
};

export const ACCESS_KEY = "ap_resources_access";

export function fileUrl(fileId, { download = false } = {}) {
    const params = new URLSearchParams();
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) params.set("access", token);
    if (download) params.set("download", "true");
    const qs = params.toString();
    return `${API}/client-resources/files/${fileId}${qs ? `?${qs}` : ""}`;
}

export function assetUrl(path) {
    return path ? `${process.env.REACT_APP_BACKEND_URL}${path}` : null;
}

// Returns an embeddable player URL for YouTube / Vimeo links, else null.
export function videoEmbedUrl(raw) {
    try {
        const u = new URL(raw);
        const host = u.hostname.replace(/^www\.|^m\./, "");
        let id = null;
        if (host === "youtu.be") id = u.pathname.slice(1).split("/")[0];
        if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
            id = u.searchParams.get("v") || (u.pathname.match(/\/(embed|shorts|live)\/([^/?]+)/) || [])[2];
        }
        if (id) return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`;
        if (host === "vimeo.com" || host === "player.vimeo.com") {
            const parts = u.pathname.split("/").filter(Boolean).filter((p) => p !== "video");
            const vid = parts.find((p) => /^\d+$/.test(p));
            if (!vid) return null;
            const hash = u.searchParams.get("h") || parts[parts.indexOf(vid) + 1];
            return `https://player.vimeo.com/video/${vid}?autoplay=1${hash && /^[a-z0-9]+$/i.test(hash) ? `&h=${hash}` : ""}`;
        }
    } catch (e) {
        return null;
    }
    return null;
}

// Audio links: direct audio file → <audio>; SoundCloud → embed; else null (open in new tab).
export function audioSource(raw) {
    if (!raw) return null;
    if (/\.(mp3|m4a|aac|wav|ogg)(\?|$)/i.test(raw)) return { kind: "audio", src: raw };
    if (/soundcloud\.com/i.test(raw)) {
        return { kind: "iframe", src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(raw)}&color=%234A4A30&visual=false` };
    }
    return null;
}

export function formatApiError(e) {
    const d = e?.response?.data?.detail;
    if (!d) return e?.message || "Something went wrong. Please try again.";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
    return String(d);
}

export function formatSize(bytes) {
    if (!bytes) return "";
    return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

const CHUNK = 4 * 1024 * 1024;

// Chunked upload so large PDFs and MP3s pass proxy body limits.
export async function uploadChunked(file, kind, onProgress) {
    const init = new FormData();
    init.append("filename", file.name);
    init.append("content_type", file.type || "");
    init.append("size", String(file.size));
    init.append("kind", kind);
    const { data } = await http.post("/admin/uploads/init", init);
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    for (let i = 0; i < total; i += 1) {
        const fd = new FormData();
        fd.append("index", String(i));
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
        await http.post(`/admin/uploads/${data.upload_id}/chunk`, fd);
        onProgress?.(Math.round(((i + 1) / total) * 90));
    }
    const { data: done } = await http.post(`/admin/uploads/${data.upload_id}/complete`);
    onProgress?.(100);
    return done;
}
